import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { Injector, inject } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, Observable, from, throwError } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { SessionService } from "../authentication/session.service";
import { MyRegApiError } from "../class/my-reg-api-error";
import { TypeOf } from "../class/type-of";
import { isDevelopment } from "../constants/is-development";
import { APP_TRANSLATION_TEMPLATE } from "../constants/translate";
import { ToastType } from "../enums/toast-type.enum";
import { AppHttpErrorResponse } from "../models/app-http-error-response.model";
import { ToastService } from "../services/toast-service.service";
import { UriComponent } from "../types/uri-component.type";
import { AppSettings } from "./../config/app-settings.config";

export const appHttpInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);
  if (request.url.includes(environment.cdnUrl)) {
    request = request.clone({
      headers: request.headers /**/
        .set("Cache-Control", "no-cache, no-store, must-revalidate")
        .set("Pragma", "no-cache")
        .set("Expires", "0"),
    });
  } else if (request.url.includes(environment.apiUrl)) {
    request = request.clone({
      withCredentials: true,
    });
  }
  return next(request).pipe(
    map((event: HttpEvent<unknown>) => event),
    catchError((e: unknown) => _parseErrorBlob(e)),
    catchError((e: unknown) => (e && TypeOf.isObject(e) ? _handleHttpErrorResponse(injector, e, request) : throwError(() => e)))
  );
};

function _parseErrorBlob(errorResponse: unknown): Observable<never> {
  if (
    errorResponse &&
    errorResponse instanceof HttpErrorResponse &&
    errorResponse.error !== null &&
    errorResponse.error !== undefined &&
    errorResponse.error instanceof Blob
  ) {
    // transform the error blob content to add more useful information, such as status code
    return from(errorResponse.error.text()).pipe(
      map((text) => {
        try {
          return <unknown>JSON.parse(text);
        } catch {
          return text;
        }
      }),
      catchError(() => throwError(() => errorResponse)),
      mergeMap((blobContent) =>
        throwError(() => {
          const { status, message, url } = errorResponse;
          const wrappedBlobContent = new MyRegApiError({ status, message, body: blobContent });
          return new AppHttpErrorResponse({
            ...errorResponse,
            url: url ?? undefined,
            _parsedBlob: blobContent,
            // rebuild blob for nswagstudio's client.ts
            error: new Blob([JSON.stringify(wrappedBlobContent)], { type: "application/json" }),
          });
        })
      )
    );
  }
  return throwError(() => errorResponse);
}

/**
 * Handles errors returned by the server
 * This code is reached only after the user is authenticated
 */
function _handleHttpErrorResponse(injector: Injector, e: Partial<AppHttpErrorResponse>, request: HttpRequest<unknown>): Observable<never> {
  const isOnline: boolean | undefined | null = navigator.onLine;
  if (typeof isOnline === "boolean" && !isOnline) {
    _showNoInternetToast(injector);
    return EMPTY;
  }

  const status: number = Number(e.status);

  // Mark as intercepted
  e._wasInterceptedByApp = true;

  // print blob content on console
  if (e._parsedBlob && isDevelopment) {
    console.error(TypeOf.isObject(e._parsedBlob) ? Object.assign({}, e._parsedBlob) : e._parsedBlob);
  }

  // Part of the login flow
  if (request.url.includes(AppSettings.sessionsUserInfoUrl) && status === HttpStatusCode.Unauthorized) {
    // Regular flow
    return throwError(() => e);
  }

  // Some Service Protect Error Messages are handled outside the interceptor
  if (
    [HttpStatusCode.ServiceUnavailable, HttpStatusCode.InternalServerError, 499].some((code) => code === status) &&
    [AppSettings.isEntitledForServiceProtect, AppSettings.serviceProtectConfigurationText].some((url) => request.url.includes(url))
  ) {
    // Regular flow
    return throwError(() => e);
  }

  // Uploader handles upload errors
  if (
    [
      AppSettings.ibaseUserFileUploadUrl,
      AppSettings.adminServiceProtectFileUploadUrl,
      AppSettings.createMassUploadUrl,
      AppSettings.importFileMassUploadUrl,
      AppSettings.importUserCompaniesUrl,
      AppSettings.serviceProtectConfigurationFileUrl,
    ].some((url) => request.url.includes(url))
  ) {
    // Regular flow
    return throwError(() => e);
  }

  // This is used to load images using unconventional ways, it's just better to let it go through
  if (request.url.includes(AppSettings.getServiceProtectFileUrl)) {
    // Regular flow
    return throwError(() => e);
  }

  if (
    status === HttpStatusCode.NotFound &&
    [AppSettings.exportDeliveryNoteSerialNumberFileUrl, AppSettings.maintenanceMessageUrl].some((url) => request.url.includes(url))
  ) {
    // Regular flow
    return throwError(() => e);
  }

  if (request.url.includes("/Countries/") && !request.url.endsWith("en")) {
    // Regular flow
    return throwError(() => e);
  }

  let statusText: string = HttpStatusCode[status] ?? "Unknown Error";
  let message: string = e.message ?? "";
  let url: string = e.url ?? "";
  let sendMail = false;

  // translation file
  if (request.url.includes("i18n")) {
    const lang = request.url.match(/.*\/(.*)\.json/)?.[1];
    if (lang === APP_TRANSLATION_TEMPLATE) {
      return throwError(() => e);
    }
    _showHttpErrorToast(injector, statusText, lang ? `Language "${lang}"` : message, status);
    return throwError(() => e);
  }

  if (request.url.indexOf(AppSettings.getDataRecordUrl) > -1) {
    const registrationId = request.url.match(/\d+/)?.[0];

    if (status === HttpStatusCode.Forbidden) {
      message = `Missing permission to view registration id ${registrationId}`;
      statusText = "ERROR.MESSAGES.REGISTRATION_FORBIDDEN";
      url = e.url ?? "";
      sendMail = true;
      _navigateAfterError(injector, "/error", statusText, message, url, sendMail, status);
      return throwError(() => e);
    }

    if (status === HttpStatusCode.NotFound) {
      message = `Registration with id: ${registrationId} was not found`;
      statusText = "ERROR.NOT_FOUND";
      url = e.url ?? "";
      sendMail = true;
      _navigateAfterError(injector, "/error", statusText, message, url, sendMail, status);
      return throwError(() => e);
    }
  }

  if (status === HttpStatusCode.Unauthorized) {
    injector.get(SessionService).userSession = null;
    injector
      .get(Router)
      .navigate(["/error/session-expired"], { skipLocationChange: true })
      .catch(() => false)
      .then((result) => {
        if (!result) {
          message = "Session timeout, please login again";
          statusText = "ERROR.UNAUTHORIZED";
          _showHttpErrorToast(injector, statusText, message, status);
        }
      });
    return throwError(() => e);
  }

  if (status === HttpStatusCode.Forbidden) {
    _navigateAfterError(injector, "/error", statusText, message, url, false, status);
    return throwError(() => e);
  }

  if (status === HttpStatusCode.UnprocessableEntity) {
    _showUnprocessableEntityToast(injector);
    return throwError(() => e);
  }

  // No containers available
  if (status === HttpStatusCode.BadGateway || status === HttpStatusCode.ServiceUnavailable) {
    message = `Error getting ${url}`;
    statusText = "ERROR.MESSAGES.SERVICE_UNAVAILABLE";
    _navigateAfterError(injector, "/error", statusText, message, url, sendMail, status);
    return throwError(() => e);
  }

  if (status === HttpStatusCode.InternalServerError) {
    message = `Error getting ${url}`;
    statusText = "ERROR.UNIDENTIFIED_ERROR";
  }

  _showHttpErrorToast(injector, statusText, message, status);
  return throwError(() => e);
}

/**
 * Handle HttpErrorResponse by navigating to the given url
 *
 * @param url where to redirect the user after catching the error object
 */
async function _navigateAfterError(
  injector: Injector,
  errorUrl: string,
  statusText: string,
  message: string,
  url: string,
  sendMail: boolean,
  status: number | undefined
): Promise<boolean> {
  let queryParams: Record<string, UriComponent | undefined> = {};
  if (errorUrl === "/error") {
    // The error status and message is shown in the error page
    queryParams = { statusText, message, url, sendMail, status };
  } else {
    // The error status and message is shown in a small popup
    _showHttpErrorToast(injector, statusText, message, status);
  }
  const router = injector.get(Router);
  let result: boolean;
  try {
    result = await router.navigate([errorUrl], { queryParams, skipLocationChange: true });
  } catch {
    result = false;
  }
  if (!result) {
    _showHttpErrorToast(injector, statusText, message, status);
  }
  return result;
}

function _showNoInternetToast(injector: Injector): void {
  const translate = injector.get(TranslateService);
  const toast = injector.get(ToastService);
  toast.showDismissibleToast(translate.instant("ERROR.PWA.NO_INTERNET"), ToastType.Error, {
    testId: "noInternetToast",
    text: `<br/>${translate.instant("ERROR.PWA.PLEASE_RECONNECT")}`,
    iconClass: "icon-wifi-disconnected",
  });
}

function _showHttpErrorToast(injector: Injector, statusText: string, message: string, status: number | undefined): void {
  const translate = injector.get(TranslateService);
  const toast = injector.get(ToastService);

  if (!statusText?.trim() || statusText?.trim().toUpperCase() === "OK") {
    statusText = Number(status) ? (status?.toString() ?? "Unknown") : "Unknown";
  }

  try {
    statusText = statusText ? (translate.instant(statusText) ?? statusText) : statusText;
  } catch (err: unknown) {
    console.error(err);
  }

  try {
    message = message ? (translate.instant(message) ?? message) : message;
  } catch (err: unknown) {
    console.error(err);
  }

  toast.showToast(`${statusText}${message ? ":" : ""}`, ToastType.Error, {
    testId: "submitFormError",
    text: message,
  });
}

function _showUnprocessableEntityToast(injector: Injector): void {
  const translate = injector.get(TranslateService);
  const toast = injector.get(ToastService);
  toast.showToast(translate.instant("ERROR.MESSAGES.UNPROCESSABLE_ENTITY"), ToastType.Error, { testId: "submitFormError" });
}
